import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Trash2 } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionHeader from "@/components/site/SectionHeader";
import BrandStripe from "@/components/site/BrandStripe";
import { STYLES } from "@/data/products";
import useSEO from "@/hooks/useSEO";

const WHOLESALE_PRICE = 32.5; // NZD per unit — placeholder, update as needed
const RETAIL_PRICE = 65.0;
const STRENGTHS = [
  { value: "+1.0", bg: "var(--ge-beige)",  fg: "var(--ge-navy)" },
  { value: "+1.5", bg: "var(--ge-yellow)", fg: "var(--ge-navy)" },
  { value: "+2.0", bg: "var(--ge-red)",    fg: "#fff" },
  { value: "+2.5", bg: "var(--ge-green)",  fg: "#fff" },
  { value: "+3.0", bg: "var(--ge-navy)",   fg: "#fff" },
];

const emptyQty = () => Object.fromEntries(STRENGTHS.map((s) => [s.value, 0]));

export default function Order() {
  useSEO({
    title: "Wholesale Order Form",
    description: "Place a wholesale order for Good Eyes reading glasses. Eight styles, 30 colourways, five reading strengths. We'll email confirmation and dispatch details to your pharmacy.",
    path: "/order",
  });
  // qty state keyed by `${styleSlug}:${colourSlug}` → { "+1.0": 0, ... }
  const [qty, setQty] = useState({});
  const [buyer, setBuyer] = useState({ name: "", pharmacy: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const setCell = (key, str, val) => {
    const v = Math.max(0, Math.min(999, parseInt(val || 0, 10) || 0));
    setQty((q) => ({ ...q, [key]: { ...(q[key] || emptyQty()), [str]: v } }));
  };

  const addRowFullSet = (key) => {
    setQty((q) => {
      const row = { ...(q[key] || emptyQty()) };
      for (const st of STRENGTHS) {
        row[st.value] = Math.min(999, (row[st.value] || 0) + 1);
      }
      return { ...q, [key]: row };
    });
  };

  const addFullSet = (strengthValue, multiplier = 1) => {
    setQty((q) => {
      const next = { ...q };
      for (const s of STYLES) {
        for (const v of s.variants) {
          const key = `${s.slug}:${v.slug}`;
          const row = { ...(next[key] || emptyQty()) };
          if (strengthValue === "ALL") {
            for (const st of STRENGTHS) {
              row[st.value] = Math.min(999, (row[st.value] || 0) + multiplier);
            }
          } else {
            row[strengthValue] = Math.min(999, (row[strengthValue] || 0) + multiplier);
          }
          next[key] = row;
        }
      }
      return next;
    });
    toast.success(
      strengthValue === "ALL"
        ? `Added a full set × all 5 strengths (${STYLES.reduce((n,s)=>n+s.variants.length,0) * STRENGTHS.length} units).`
        : `Added a full set at ${strengthValue} (${STYLES.reduce((n,s)=>n+s.variants.length,0)} units).`
    );
  };

  const { rowTotals, styleTotals, grandUnits, grandWholesale, grandRetail } = useMemo(() => {
    const rowTotals = {};
    const styleTotals = {};
    let grandUnits = 0;
    for (const s of STYLES) {
      let sUnits = 0;
      for (const v of s.variants) {
        const key = `${s.slug}:${v.slug}`;
        const row = qty[key] || emptyQty();
        const rUnits = STRENGTHS.reduce((sum, st) => sum + (row[st.value] || 0), 0);
        rowTotals[key] = rUnits;
        sUnits += rUnits;
      }
      styleTotals[s.slug] = sUnits;
      grandUnits += sUnits;
    }
    return {
      rowTotals, styleTotals, grandUnits,
      grandWholesale: grandUnits * WHOLESALE_PRICE,
      grandRetail:    grandUnits * RETAIL_PRICE,
    };
  }, [qty]);

  const clearAll = () => setQty({});

  const submit = async (e) => {
    e.preventDefault();
    if (grandUnits === 0) { toast.error("Add at least one item to your order."); return; }
    if (!buyer.name || !/\S+@\S+\.\S+/.test(buyer.email)) {
      toast.error("Please add your name and a valid email."); return;
    }
    setSubmitting(true);
    // Flatten into line items
    const lines = [];
    for (const s of STYLES) {
      for (const v of s.variants) {
        const key = `${s.slug}:${v.slug}`;
        const row = qty[key] || emptyQty();
        for (const st of STRENGTHS) {
          const q = row[st.value];
          if (q > 0) lines.push({
            style: s.name, colour: v.color, strength: st.value, qty: q,
            wholesale: WHOLESALE_PRICE, retail: RETAIL_PRICE,
          });
        }
      }
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          buyer,
          lines,
          totals: { units: grandUnits, wholesale: grandWholesale, retail: grandRetail },
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Order failed");
      }
      toast.success("Order submitted. We'll be in touch shortly.");
      setQty({}); setBuyer({ name: "", pharmacy: "", email: "", phone: "", notes: "" });
    } catch (err) {
      toast.error(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-[color:var(--ge-navy)]" data-testid="order-page">
      <Nav />
      <main>
        <section className="relative pt-40 md:pt-48 pb-14">
          <div className="ge-container">
            <SectionHeader
              kicker="Wholesale Order Form"
              title={<>Place an <span style={{ color: "var(--ge-red)" }}>order</span>.</>}
              subtitle={<>Eight styles. {STYLES.reduce((n,s)=>n+s.variants.length,0)} colourways. Five strengths. Add quantities below and we'll email your order to sales@goodeyes.co.nz for confirmation and dispatch.</>}
            />
          </div>
          <div className="absolute bottom-0 left-0 right-0"><BrandStripe height={22} /></div>
        </section>

        <section className="py-14 md:py-20">
          <form className="ge-container" onSubmit={submit}>
            {STYLES.map((s) => (
              <div key={s.slug} className="mb-10 rounded-2xl border border-[color:var(--ge-navy)]/10 overflow-hidden">
                {/* Style header */}
                <div className="grid grid-cols-12 gap-4 md:gap-6 items-center bg-[color:var(--ge-cream)] p-5 md:p-6">
                  <div className="col-span-3 md:col-span-2 flex items-center justify-center">
                    <img src={s.variants[0].img} alt={s.name} className="max-h-32 md:max-h-44 w-auto" />
                  </div>
                  <div className="col-span-6 md:col-span-7">
                    <h3 className="display-font tight-cap font-extrabold text-[color:var(--ge-navy)] text-2xl md:text-3xl">
                      {s.name}
                    </h3>
                    <p className="text-xs md:text-sm font-light text-[color:var(--ge-navy)]/70 max-w-lg line-clamp-2">{s.tagline}</p>
                  </div>
                  <div className="col-span-3 md:col-span-3 text-right">
                    <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60">Style total</div>
                    <div className="text-2xl md:text-3xl font-bold" style={{ color: styleTotals[s.slug] > 0 ? "var(--ge-red)" : "var(--ge-navy)" }}>
                      {styleTotals[s.slug] || 0}
                    </div>
                  </div>
                </div>

                {/* Column headers */}
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60 border-b border-[color:var(--ge-navy)]/10">
                  <div className="col-span-4">Colourway</div>
                  {STRENGTHS.map((st) => (
                    <div key={st.value} className="col-span-1 text-center">{st.value}</div>
                  ))}
                  <div className="col-span-1 text-center">Add all</div>
                  <div className="col-span-2 text-right">Row total</div>
                </div>

                {/* Colour rows */}
                <div className="divide-y divide-[color:var(--ge-navy)]/10">
                  {s.variants.map((v) => {
                    const key = `${s.slug}:${v.slug}`;
                    const row = qty[key] || emptyQty();
                    return (
                      <div key={v.slug} className="grid grid-cols-12 gap-4 px-4 md:px-6 py-4 items-center">
                        <div className="col-span-12 md:col-span-4 flex items-center gap-4">
                          <img src={v.img} alt={v.color} className="h-20 w-32 md:h-28 md:w-44 object-contain product-img shrink-0" />
                          <div className="min-w-0">
                            <div className="text-sm md:text-base font-bold text-[color:var(--ge-navy)]">{v.color}</div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: v.swatch, boxShadow: "inset 0 0 0 1px rgba(0,31,91,0.15)" }} />
                              <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[color:var(--ge-navy)]/60">{s.name}</span>
                            </div>
                          </div>
                        </div>
                        {STRENGTHS.map((st) => (
                          <div key={st.value} className="col-span-2 md:col-span-1">
                            <label className="md:hidden text-[9px] uppercase tracking-[0.22em] font-semibold text-[color:var(--ge-navy)]/60 flex items-center gap-1">
                              <span className="w-2 h-2 rounded-full" style={{ background: st.bg }} />
                              {st.value}
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="999"
                              value={row[st.value] || ""}
                              placeholder="0"
                              onChange={(e) => setCell(key, st.value, e.target.value)}
                              data-testid={`order-qty-${s.slug}-${v.slug}-${st.value.replace(".","-")}`}
                              className={`w-full text-center rounded-lg border py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[color:var(--ge-navy)]/40 transition-colors ${
                                (row[st.value] || 0) > 0
                                  ? "border-[color:var(--ge-navy)] bg-white text-[color:var(--ge-navy)]"
                                  : "border-[color:var(--ge-navy)]/15 bg-[color:var(--ge-cream)]/40 text-[color:var(--ge-navy)]/70"
                              }`}
                            />
                          </div>
                        ))}
                        <div className="col-span-4 md:col-span-1 flex md:justify-center order-last md:order-none">
                          <button
                            type="button"
                            onClick={() => addRowFullSet(key)}
                            data-testid={`order-rowfullset-${s.slug}-${v.slug}`}
                            className="inline-flex items-center gap-1 rounded-full border border-[color:var(--ge-navy)]/20 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold text-[color:var(--ge-navy)]/80 hover:bg-[color:var(--ge-navy)] hover:text-white hover:border-[color:var(--ge-navy)] transition-colors whitespace-nowrap"
                            title={`Add one of every strength for ${v.color}`}
                          >
                            + Full set
                          </button>
                        </div>
                        <div className="col-span-2 md:col-span-2 text-right">
                          <div className="text-[10px] md:hidden uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60">Row</div>
                          <div className="text-lg md:text-xl font-bold" style={{ color: rowTotals[key] > 0 ? "var(--ge-red)" : "var(--ge-navy)/50" }}>
                            {rowTotals[key] || 0}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Full-Set quick add */}
            <div className="mb-10 rounded-2xl overflow-hidden border border-[color:var(--ge-navy)]/10" data-testid="order-full-set">
              <div className="grid grid-cols-12 gap-4 md:gap-6 items-center p-5 md:p-6" style={{ background: "var(--ge-navy)", color: "#fff" }}>
                <div className="col-span-12 md:col-span-7">
                  <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: "var(--ge-yellow)" }}>Quick Add</div>
                  <h3 className="display-font tight-cap font-extrabold text-2xl md:text-3xl mt-1">The Full Set</h3>
                  <p className="text-xs md:text-sm font-light text-white/75 max-w-lg mt-1">
                    One of every colourway across all eight styles, {STYLES.reduce((n,s)=>n+s.variants.length,0)} pairs.
                    Pick a strength to add a starter kit, or add the complete range across all five strengths.
                  </p>
                </div>
                <div className="col-span-12 md:col-span-5 md:text-right">
                  <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-white/60">Per full set</div>
                  <div className="text-2xl md:text-3xl font-bold" style={{ color: "var(--ge-yellow)" }}>
                    {STYLES.reduce((n,s)=>n+s.variants.length,0)} pairs
                  </div>
                </div>
              </div>
              <div className="p-4 md:p-6 bg-white">
                <div className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[color:var(--ge-navy)]/60 mb-3">
                  Add one of each colourway at…
                </div>
                <div className="flex flex-wrap gap-2 md:gap-3">
                  {STRENGTHS.map((st) => (
                    <button
                      key={st.value}
                      type="button"
                      onClick={() => addFullSet(st.value)}
                      data-testid={`order-fullset-${st.value.replace(".","-")}`}
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold border border-[color:var(--ge-navy)]/15 hover:border-[color:var(--ge-navy)] transition-colors"
                      style={{ color: "var(--ge-navy)" }}
                    >
                      <span className="w-2.5 h-2.5 rounded-full" style={{ background: st.bg, boxShadow: "inset 0 0 0 1px rgba(0,31,91,0.15)" }} />
                      {st.value}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => addFullSet("ALL")}
                    data-testid="order-fullset-all"
                    className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold text-white hover:brightness-110 transition"
                    style={{ background: "var(--ge-red)" }}
                  >
                    Full range × all strengths
                  </button>
                </div>
              </div>
            </div>

            {/* Totals + buyer + submit */}
            <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 rounded-2xl border border-[color:var(--ge-navy)]/10 bg-white p-6 md:p-8">
                <div className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">Your Details</div>
                <div className="mt-1 h-[3px] w-10 rounded-full" style={{ background: "var(--ge-yellow)" }} />
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Contact name*"  value={buyer.name}     onChange={(v)=>setBuyer({...buyer,name:v})}     tid="order-buyer-name" />
                  <Field label="Pharmacy / Business" value={buyer.pharmacy} onChange={(v)=>setBuyer({...buyer,pharmacy:v})} tid="order-buyer-pharmacy" />
                  <Field label="Email*"         value={buyer.email}    onChange={(v)=>setBuyer({...buyer,email:v})}    tid="order-buyer-email" />
                  <Field label="Phone"          value={buyer.phone}    onChange={(v)=>setBuyer({...buyer,phone:v})}    tid="order-buyer-phone" />
                </div>
                <div className="mt-4">
                  <label className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">Notes / delivery instructions</label>
                  <textarea rows={3} value={buyer.notes} onChange={(e)=>setBuyer({...buyer,notes:e.target.value})} data-testid="order-buyer-notes"
                    className="mt-2 w-full bg-white border border-[color:var(--ge-navy)]/15 rounded-lg px-4 py-3 text-sm text-[color:var(--ge-navy)] resize-y" />
                </div>
              </div>

              <div className="lg:col-span-5 rounded-2xl p-6 md:p-8 text-white" style={{ background: "var(--ge-navy)" }}>
                <div className="text-[11px] uppercase tracking-[0.28em] font-semibold" style={{ color: "var(--ge-yellow)" }}>Order Summary</div>
                <div className="mt-1 h-[3px] w-10 rounded-full" style={{ background: "var(--ge-yellow)" }} />
                <dl className="mt-6 space-y-3 text-sm">
                  <Row label="Total units" value={grandUnits} data-testid="order-total-units" />
                  <Row label={`Wholesale × $${WHOLESALE_PRICE.toFixed(2)}`} value={`$${grandWholesale.toFixed(2)} NZD`} data-testid="order-total-wholesale" />
                  <Row label={`Retail value × $${RETAIL_PRICE.toFixed(2)}`} value={`$${grandRetail.toFixed(2)} NZD`} muted data-testid="order-total-retail" />
                </dl>
                <div className="mt-8 flex gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="order-submit"
                    className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--ge-yellow)] text-[color:var(--ge-navy)] px-6 py-4 text-xs uppercase tracking-[0.24em] font-semibold hover:brightness-110 transition disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Submit order"} <ArrowRight size={14} />
                  </button>
                  <button type="button" onClick={clearAll} data-testid="order-clear"
                    className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-4 text-xs uppercase tracking-[0.24em] font-semibold text-white/90 hover:bg-white/10 transition">
                    <Trash2 size={14} /> Clear
                  </button>
                </div>
                <p className="mt-5 text-[11px] text-white/70">
                  Submitting emails your order to sales@goodeyes.co.nz. We'll reply within one business day with confirmation and invoice.
                </p>
              </div>
            </div>
          </form>
        </section>
      </main>
      <Footer />
    </div>
  );
}

const Field = ({ label, value, onChange, tid }) => (
  <div>
    <label className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[color:var(--ge-navy)]/70">{label}</label>
    <input type="text" value={value} onChange={(e)=>onChange(e.target.value)} data-testid={tid}
      className="mt-2 w-full bg-white border border-[color:var(--ge-navy)]/15 rounded-lg px-4 py-3 text-sm text-[color:var(--ge-navy)]" />
  </div>
);

const Row = ({ label, value, muted, ...rest }) => (
  <div className="flex items-baseline justify-between border-t border-white/15 pt-3" {...rest}>
    <dt className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${muted ? "text-white/60" : "text-white/80"}`}>{label}</dt>
    <dd className={`text-lg font-bold ${muted ? "text-white/80" : "text-white"}`}>{value}</dd>
  </div>
);
